"use client";

import { type ReactNode, type SyntheticEvent, useEffect, useRef } from "react";
import { createPortal } from "react-dom";

/**
 * Shared modal/dialog frame for AICheck dialogs.
 *
 * Consolidates the boilerplate that was previously duplicated across every
 * dialog: the fixed backdrop, the click-to-close overlay, Escape handling,
 * body/HTML scroll locking, optional portal rendering into a foreign document
 * (popup window) and backdrop scroll trapping.
 *
 * The visual look of each dialog is preserved by passing the exact
 * `backdropClassName` / `panelClassName` strings the call site used before.
 * Header, body and footer stay in `children` so nothing inside the panel moves.
 */

export type DialogScrollLock = "none" | "body" | "html-and-body";

export type DialogShellProps = {
  onClose: () => void;
  children: ReactNode;
  /** Full className for the fixed backdrop container. */
  backdropClassName?: string;
  /** Full className for the inner panel (rendered under `relative z-10`). */
  panelClassName: string;
  /** className applied to the backdrop overlay (button or div). */
  backdropOverlayClassName?: string;
  /** Value for aria-labelledby on the dialog container. */
  ariaLabelledBy?: string;
  /** Close when the backdrop overlay is clicked. Default true. */
  closeOnBackdrop?: boolean;
  /** aria-label for the backdrop close button. */
  backdropAriaLabel?: string;
  /** When true the backdrop close button is disabled. */
  backdropDisabled?: boolean;
  /** Whether pressing Escape closes the dialog. Default true. */
  escapeToClose?: boolean;
  /** Body/HTML scroll locking while open. Default "none". */
  scrollLock?: DialogScrollLock;
  /**
   * Document used for scroll-lock target, key listener and portal.
   * Falls back to the dialog's own ownerDocument when omitted.
   */
  ownerDocument?: Document | null;
  /** Render into `ownerDocument.body` via a portal when available. */
  portalToOwnerBody?: boolean;
  /**
   * Prevent wheel/touch scroll from leaking to the backdrop while still
   * allowing scrolling inside the panel.
   */
  trapBackdropScroll?: boolean;
};

export function DialogShell({
  onClose,
  children,
  backdropClassName = "fixed inset-0 z-[150] flex items-center justify-center bg-black/40 p-4",
  panelClassName,
  backdropOverlayClassName = "absolute inset-0",
  ariaLabelledBy,
  closeOnBackdrop = true,
  backdropAriaLabel,
  backdropDisabled = false,
  escapeToClose = true,
  scrollLock = "none",
  ownerDocument = null,
  portalToOwnerBody = false,
  trapBackdropScroll = false,
}: DialogShellProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  // Escape to close + scroll lock. Both target the resolved document so that
  // dialogs rendered into a popup window keep working.
  useEffect(() => {
    const doc =
      ownerDocument ?? containerRef.current?.ownerDocument ?? document;

    let handleKeyDown: ((event: KeyboardEvent) => void) | null = null;
    if (escapeToClose) {
      handleKeyDown = (event: KeyboardEvent) => {
        if (event.key === "Escape") onClose();
      };
      doc.addEventListener("keydown", handleKeyDown);
    }

    let restoreScroll: (() => void) | null = null;
    if (scrollLock !== "none") {
      const bodyOverflow = doc.body.style.overflow;
      const htmlOverflow = doc.documentElement.style.overflow;
      doc.body.style.overflow = "hidden";
      if (scrollLock === "html-and-body") {
        doc.documentElement.style.overflow = "hidden";
      }
      restoreScroll = () => {
        doc.body.style.overflow = bodyOverflow;
        if (scrollLock === "html-and-body") {
          doc.documentElement.style.overflow = htmlOverflow;
        }
      };
    }

    return () => {
      if (handleKeyDown) doc.removeEventListener("keydown", handleKeyDown);
      if (restoreScroll) restoreScroll();
    };
  }, [escapeToClose, onClose, ownerDocument, scrollLock]);

  const handleBackdropScroll = (event: SyntheticEvent<HTMLDivElement>) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target || !panelRef.current?.contains(target)) {
      event.preventDefault();
    }
    event.stopPropagation();
  };

  const dialog = (
    <div
      ref={containerRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby={ariaLabelledBy}
      className={backdropClassName}
      onWheel={trapBackdropScroll ? handleBackdropScroll : undefined}
      onTouchMove={trapBackdropScroll ? handleBackdropScroll : undefined}
    >
      {closeOnBackdrop ? (
        <button
          type="button"
          aria-label={backdropAriaLabel}
          className={backdropOverlayClassName}
          disabled={backdropDisabled}
          onClick={onClose}
        />
      ) : (
        <div className={backdropOverlayClassName} />
      )}
      <div ref={panelRef} className={panelClassName}>
        {children}
      </div>
    </div>
  );

  if (portalToOwnerBody && ownerDocument?.body) {
    return createPortal(dialog, ownerDocument.body);
  }

  return dialog;
}
